/* ═══════════════════════════════════════════════════════════════════════════
   HCS Product Card — shared interactive behavior

   ALL handlers use document-level event delegation. Nothing is bound to
   individual elements, so Elixir's cart drawer `replaceChild` DOM swap in
   updateCartDrawer() cannot wipe handlers. Sections that render any
   .hcs-card descendants automatically get working interactions.

   Features covered:
     - Heat bar / size pills / toggle bar / dropdown fallback variant pickers
     - Variant resolution from inline JSON
     - ATC button: desktop hover-to-activate, click-to-add; mobile tap-to-
       activate, tap-again-to-add; mobile with sheet enabled opens sheet
     - AJAX cart add with cart drawer section re-render
     - Mobile bottom sheet: open/close, drag-to-dismiss, backdrop close,
       escape key, variant controls cloned from source card, sync-back
     - Card click → product page navigation
     - Outside click closes any active card

   Section requirements (for the calling section markup):
     - Section root has data-hcs-scope (any value) AND id
     - Card root has class "hcs-card" and data-hcs-card data-hcs-url="..."
     - Variant wrap has data-hcs-atb-wrap
     - ATC button has data-hcs-add data-hcs-vid data-hcs-has-variants
     - Variants JSON in <script data-hcs-variants>
     - Variant picker elements have data-hcs-vopt="{option_index}"
     - To opt into bottom sheet on mobile multi-col layouts, render the
       hcs-product-card-sheet snippet AND set data-hcs-use-sheet-m="1" +
       data-m-cols="N" on the section root. Sheet activates when
       isMobileViewport && mCols > 1.

   No stopImmediatePropagation anywhere — preserves cart drawer
   bubbling-phase event delegation.
   ═══════════════════════════════════════════════════════════════════════════ */

(function () {
  'use strict';

  /* ──────────────────────────────────────────────────────────────────────
     Environment detection (computed lazily per event so viewport changes
     across rotate/resize are respected)

     Both functions use the same 750px viewport breakpoint that drives the
     CSS layout. Capability-based detection (hover: hover / pointer: fine)
     was unreliable: Chrome mobile emulation reports hover:hover true even
     when emulating an iPhone, and hybrid devices (Surface, iPad w/ keyboard,
     touch Macs) can report fine pointer at mobile widths. Viewport width
     is what the rest of the system uses, so we use it here too.
     ────────────────────────────────────────────────────────────────────── */
  function isDesktop() {
    return window.innerWidth >= 750;
  }

  function isMobileViewport() {
    return window.innerWidth < 750;
  }

  /* ──────────────────────────────────────────────────────────────────────
     Helpers — variant resolution, price formatting
     ────────────────────────────────────────────────────────────────────── */

  function gatherChosenOptions(wrap) {
    var chosen = [];
    var opts = wrap.querySelectorAll('[data-hcs-vopt]');
    for (var i = 0; i < opts.length; i++) {
      var el = opts[i];
      var idx = parseInt(el.getAttribute('data-hcs-vopt'), 10);
      if (isNaN(idx)) continue;

      /* Skip controls hidden by the responsive layout (display:none → null
         offsetParent), so each option resolves from whichever control the
         panel is actually showing — the pill bar on wide cards, the native
         <select> on narrow (drawer) cards. visibility:hidden / max-height:0
         (the panel at rest) keeps a non-null offsetParent, so resolution works
         whether the panel is open or closed. */
      if (el.offsetParent === null) continue;

      if (el.tagName === 'SELECT') {
        chosen[idx] = el.value;
      } else if (el.classList.contains('hcs-card__heat-bar')) {
        var hOn = el.querySelector('.hcs-card__heat-seg--on');
        if (hOn) chosen[idx] = hOn.getAttribute('data-hcs-val');
      } else if (el.classList.contains('hcs-card__size-segs')) {
        var sOn = el.querySelector('.hcs-card__size-seg--on');
        if (sOn) chosen[idx] = sOn.getAttribute('data-hcs-val');
      } else if (el.classList.contains('hcs-card__toggle-bar')) {
        var tOn = el.querySelector('.hcs-card__toggle-seg--on');
        if (tOn) chosen[idx] = tOn.getAttribute('data-hcs-val');
      }
    }
    return chosen;
  }

  function resolveVariantObj(wrap) {
    if (!wrap) return null;
    var jsonEl = wrap.querySelector('[data-hcs-variants]');
    if (!jsonEl) return null;
    var variants;
    try {
      variants = JSON.parse(jsonEl.textContent);
    } catch (e) {
      return null;
    }
    var chosen = gatherChosenOptions(wrap);
    for (var i = 0; i < variants.length; i++) {
      var v = variants[i];
      var match = true;
      for (var j = 0; j < chosen.length; j++) {
        if (chosen[j] !== undefined && v.options[j] !== chosen[j]) {
          match = false;
          break;
        }
      }
      if (match && v.available) return v;
    }
    return null;
  }

  function resolveVariantId(wrap) {
    var v = resolveVariantObj(wrap);
    return v ? v.id : null;
  }

  function formatMoney(cents) {
    var d = (cents / 100).toFixed(2);
    return '$' + d.replace(/\.00$/, '');
  }

  function updateBtnPrice(wrap, vObj) {
    if (!wrap || !vObj) return;
    var priceEl = wrap.querySelector('.hcs-card__atb-btn-price');
    if (priceEl) priceEl.textContent = formatMoney(vObj.price);
  }

  function syncBtnForVariant(wrap) {
    if (!wrap) return;
    var btn = wrap.querySelector('[data-hcs-add]');
    var vObj = resolveVariantObj(wrap);
    if (btn) {
      if (vObj) {
        btn.setAttribute('data-hcs-vid', vObj.id);
        btn.disabled = false;
        btn.removeAttribute('aria-disabled');
        btn.classList.remove('hcs-card__atb-btn--out');
      } else {
        /* Chosen options map to an unavailable (or impossible) variant —
           never let the user add it. */
        btn.disabled = true;
        btn.setAttribute('aria-disabled', 'true');
        btn.classList.add('hcs-card__atb-btn--out');
      }
    }
    updateBtnPrice(wrap, vObj);
    markSoldOutOptions(wrap);
  }

  /* ──────────────────────────────────────────────────────────────────────
     Shared option-button selection — toggles the JS hook class (for
     gatherChosenOptions) AND the shared .hcs-sel-btn--on / aria-pressed
     across the group, then re-resolves the variant. Works for card panels
     and the cloned sheet body alike (resolves the right wrap each call).
     ────────────────────────────────────────────────────────────────────── */
  function selectOptionButton(btn, segSelector, onClass) {
    var group = btn.parentNode;
    if (!group) return;
    var segs = group.querySelectorAll(segSelector);
    for (var i = 0; i < segs.length; i++) {
      var on = segs[i] === btn;
      segs[i].classList.toggle(onClass, on);
      segs[i].classList.toggle('hcs-sel-btn--on', on);
      segs[i].setAttribute('aria-pressed', on ? 'true' : 'false');
    }
    var wrap = btn.closest('[data-hcs-atb-wrap], .hcs-sheet__body');
    if (!wrap) return;
    if (wrap.classList.contains('hcs-sheet__body')) {
      syncSheetFromBody(wrap.closest('.hcs-sheet-overlay'));
    } else {
      syncBtnForVariant(wrap);
    }
  }

  /* ──────────────────────────────────────────────────────────────────────
     Mark impossible option values sold-out (mirrors the PDP picker).
     For each option button, check whether ANY available variant exists for
     the current selection of the OTHER options + that button's value. If
     not (and the button isn't the current selection), flag it
     .hcs-sel-btn--soldout + aria-disabled so it can't be chosen.
     ────────────────────────────────────────────────────────────────────── */
  function markSoldOutOptions(wrap) {
    if (!wrap) return;
    var jsonEl = wrap.querySelector('[data-hcs-variants]');
    if (!jsonEl) return;
    var variants;
    try {
      variants = JSON.parse(jsonEl.textContent);
    } catch (e) {
      return;
    }
    var chosen = gatherChosenOptions(wrap);
    var groups = wrap.querySelectorAll('[data-hcs-vopt]');
    for (var g = 0; g < groups.length; g++) {
      var group = groups[g];
      if (group.tagName === 'SELECT') continue; /* dropdown fallback — leave as-is */
      var pos = parseInt(group.getAttribute('data-hcs-vopt'), 10);
      if (isNaN(pos)) continue;
      var btns = group.querySelectorAll('.hcs-sel-btn');
      for (var b = 0; b < btns.length; b++) {
        var btn = btns[b];
        var val = btn.getAttribute('data-hcs-val');
        var hasAvail = false;
        for (var v = 0; v < variants.length; v++) {
          var variant = variants[v];
          if (!variant.available) continue;
          var ok = true;
          for (var j = 0; j < chosen.length; j++) {
            var want = (j === pos) ? val : chosen[j];
            if (want !== undefined && want !== null && variant.options[j] !== want) {
              ok = false;
              break;
            }
          }
          if (ok) { hasAvail = true; break; }
        }
        var isSelected = btn.classList.contains('hcs-sel-btn--on');
        if (!hasAvail && !isSelected) {
          btn.classList.add('hcs-sel-btn--soldout');
          btn.setAttribute('aria-disabled', 'true');
        } else {
          btn.classList.remove('hcs-sel-btn--soldout');
          btn.removeAttribute('aria-disabled');
        }
      }
    }
  }

  /* ──────────────────────────────────────────────────────────────────────
     Default-to-in-stock — when the server-rendered default selection (e.g.
     Medium heat) maps to a SOLD-OUT variant, snap the option buttons to the
     first in-stock variant so the card boots addable instead of greyed.

     Init-only. Runs once on load BEFORE syncBtnForVariant; never fires on a
     user interaction, so a deliberate pick of a sold-out combo still shows
     disabled (markSoldOutOptions) rather than silently jumping elsewhere.
     Covers every picker type (heat / size / toggle / <select>) and multi-
     option products; the mobile sheet inherits it via the panel clone.
     ────────────────────────────────────────────────────────────────────── */

  function selectValuesInWrap(wrap, variant) {
    var groups = wrap.querySelectorAll('[data-hcs-vopt]');
    for (var g = 0; g < groups.length; g++) {
      var group = groups[g];
      var pos = parseInt(group.getAttribute('data-hcs-vopt'), 10);
      if (isNaN(pos)) continue;
      var want = variant.options[pos];
      if (group.tagName === 'SELECT') { group.value = want; continue; }
      /* Match the JS hook class gatherChosenOptions reads per group type. */
      var onClass = group.classList.contains('hcs-card__heat-bar') ? 'hcs-card__heat-seg--on'
        : group.classList.contains('hcs-card__size-segs') ? 'hcs-card__size-seg--on'
        : group.classList.contains('hcs-card__toggle-bar') ? 'hcs-card__toggle-seg--on' : null;
      if (!onClass) continue;
      var btns = group.querySelectorAll('.hcs-sel-btn');
      for (var b = 0; b < btns.length; b++) {
        var on = btns[b].getAttribute('data-hcs-val') === want;
        btns[b].classList.toggle(onClass, on);
        btns[b].classList.toggle('hcs-sel-btn--on', on);
        btns[b].setAttribute('aria-pressed', on ? 'true' : 'false');
      }
    }
  }

  function ensureAvailableDefault(wrap) {
    if (resolveVariantObj(wrap)) return;            /* default already addable */
    var jsonEl = wrap.querySelector('[data-hcs-variants]');
    if (!jsonEl) return;
    var variants;
    try {
      variants = JSON.parse(jsonEl.textContent);
    } catch (e) {
      return;
    }
    for (var i = 0; i < variants.length; i++) {
      if (variants[i].available) { selectValuesInWrap(wrap, variants[i]); return; }
    }
  }

  /* ──────────────────────────────────────────────────────────────────────
     Card active-state helpers
     ────────────────────────────────────────────────────────────────────── */

  function deactivateAllIn(scope) {
    if (!scope) return;
    var active = scope.querySelectorAll('.hcs-card--active');
    for (var i = 0; i < active.length; i++) {
      active[i].classList.remove('hcs-card--active');
      var ch = active[i].querySelector('.hcs-card__btn-chevron');
      if (ch && ch.textContent === '→') ch.textContent = '▾';
    }
  }

  function activateCard(card) {
    if (!card) return;
    var scope = card.closest('[data-hcs-scope]') || document;
    deactivateAllIn(scope);
    card.classList.add('hcs-card--active');
    var chevron = card.querySelector('.hcs-card__btn-chevron');
    if (chevron && chevron.textContent === '▾') chevron.textContent = '→';
  }

  function deactivateCard(card) {
    if (!card) return;
    card.classList.remove('hcs-card--active');
    var chevron = card.querySelector('.hcs-card__btn-chevron');
    if (chevron && chevron.textContent === '→') chevron.textContent = '▾';
  }

  /* ──────────────────────────────────────────────────────────────────────
     AJAX cart add with proper drawer re-render
     ────────────────────────────────────────────────────────────────────── */

  function ajaxAdd(vid, btn) {
    if (!vid) return;
    var labelEl = btn ? btn.querySelector('.hcs-card__atb-btn-label') : null;
    var origHTML = labelEl ? labelEl.innerHTML : (btn ? btn.textContent : '');
    if (labelEl) labelEl.textContent = 'Adding...';
    else if (btn) btn.textContent = 'Adding...';

    window.HCSQuickAdd(vid, btn)
      .then(function (parsedState) {
        if (labelEl) labelEl.textContent = 'Added ✓';
        else if (btn) btn.textContent = 'Added ✓';

        document.dispatchEvent(new CustomEvent('cart:updated', { detail: parsedState }));
        document.dispatchEvent(new CustomEvent('cart:open'));
        document.dispatchEvent(new CustomEvent('ajaxProduct:added'));

        setTimeout(function () {
          if (labelEl) labelEl.innerHTML = origHTML;
          else if (btn) btn.textContent = origHTML;
        }, 2200);
      })
      .catch(function () {
        if (labelEl) labelEl.textContent = 'Error';
        else if (btn) btn.textContent = 'Error';
        setTimeout(function () {
          if (labelEl) labelEl.innerHTML = origHTML;
          else if (btn) btn.textContent = origHTML;
        }, 2200);
      });
  }

  /* ──────────────────────────────────────────────────────────────────────
     Bottom sheet — open / close / populate / sync
     ────────────────────────────────────────────────────────────────────── */

  var sheetSourceBtn = null;
  var sheetScopeEl = null;

  function shouldUseSheet(scope) {
    if (!scope) return false;
    if (scope.getAttribute('data-hcs-use-sheet-m') !== '1') return false;
    var overlayId = scope.getAttribute('data-hcs-sheet-id');
    if (!overlayId) return false;
    if (!document.getElementById(overlayId)) return false;
    if (!isMobileViewport()) return false;
    var mCols = parseInt(scope.getAttribute('data-m-cols') || '1', 10);
    return mCols > 1;
  }

  /* Section wants the mobile sheet UX (regardless of which sheet implementation
     is available) — same gate as shouldUseSheet minus the legacy overlay check. */
  function wantsSheetM(scope) {
    if (!scope) return false;
    if (scope.getAttribute('data-hcs-use-sheet-m') !== '1') return false;
    var mCols = parseInt(scope.getAttribute('data-m-cols') || '1', 10);
    return mCols > 1;
  }

  function getSheetOverlay(scope) {
    var id = scope && scope.getAttribute('data-hcs-sheet-id');
    return id ? document.getElementById(id) : null;
  }

  function openSheet(card, btn) {
    var scope = card.closest('[data-hcs-scope]');
    var overlay = getSheetOverlay(scope);
    if (!overlay) return;

    var body = overlay.querySelector('.hcs-sheet__body');
    var img = overlay.querySelector('.hcs-sheet__img');
    var name = overlay.querySelector('.hcs-sheet__name');
    var price = overlay.querySelector('.hcs-sheet__price');
    var sheetAtb = overlay.querySelector('[data-hcs-sheet-atb]');
    if (!body) return;

    var wrap = btn.closest('[data-hcs-atb-wrap]');
    if (!wrap) return;
    var panel = wrap.querySelector('.hcs-card__atb-panel');
    if (!panel) return;

    /* Header population from source card */
    var srcImg = card.querySelector('.hcs-card__img--1');
    if (srcImg && img) {
      img.src = srcImg.src;
      img.alt = srcImg.alt || '';
    }
    var srcName = card.querySelector('.hcs-card__name');
    if (srcName && name) name.textContent = srcName.textContent;
    var srcPrices = card.querySelector('.hcs-card__prices');
    if (srcPrices && price) price.innerHTML = srcPrices.innerHTML;

    /* Clone variant panel contents into sheet body */
    body.innerHTML = '';
    var clone = panel.cloneNode(true);
    while (clone.firstChild) {
      body.appendChild(clone.firstChild);
    }

    /* Sync sheet ATB price/state from current selection */
    syncSheetFromBody(overlay);

    sheetSourceBtn = btn;
    sheetScopeEl = scope;
    overlay.classList.add('hcs-sheet-overlay--open');
    document.body.style.overflow = 'hidden';

    /* Reset swipe tracker */
    overlay._hcsSheetStartY = 0;
  }

  function closeSheet() {
    var overlay = sheetScopeEl ? getSheetOverlay(sheetScopeEl) : null;
    if (!overlay) {
      /* Fallback — close any open overlay */
      var any = document.querySelector('.hcs-sheet-overlay--open');
      if (any) overlay = any;
    }
    if (!overlay) return;

    overlay.classList.remove('hcs-sheet-overlay--open');
    document.body.style.overflow = '';
    sheetSourceBtn = null;
    sheetScopeEl = null;
  }

  function syncSheetFromBody(overlay) {
    var body = overlay.querySelector('.hcs-sheet__body');
    var price = overlay.querySelector('.hcs-sheet__price');
    var sheetAtb = overlay.querySelector('[data-hcs-sheet-atb]');
    if (!body || !sheetAtb) return;

    /* Reflect impossible/unavailable selections on the option buttons first,
       then gate the sheet ATB. */
    markSoldOutOptions(body);

    var vObj = resolveVariantObj(body);
    if (!vObj) {
      /* No available variant for this selection — never allow adding. */
      sheetAtb.disabled = true;
      sheetAtb.setAttribute('aria-disabled', 'true');
      return;
    }

    sheetAtb.setAttribute('data-hcs-vid', vObj.id);
    sheetAtb.disabled = false;
    sheetAtb.removeAttribute('aria-disabled');

    if (price) {
      var html = '';
      if (vObj.compare_at_price && vObj.compare_at_price > vObj.price) {
        html += '<span class="hcs-card__price--was">' + formatMoney(vObj.compare_at_price) + '</span> ';
      }
      var saleClass = vObj.compare_at_price > vObj.price ? ' hcs-card__price--sale' : '';
      html += '<span class="hcs-card__price' + saleClass + '">' + formatMoney(vObj.price) + '</span>';
      price.innerHTML = html;
    }
  }

  /* ──────────────────────────────────────────────────────────────────────
     DOCUMENT-LEVEL CLICK DELEGATION
     One listener handles every card interaction. Order matters: check the
     most specific targets first so parent-target checks don't catch them.
     ────────────────────────────────────────────────────────────────────── */

  document.addEventListener('click', function (e) {
    var target = e.target;

    /* Sheet backdrop click → close */
    var sheetOverlayHit = target.closest('.hcs-sheet-overlay');
    if (sheetOverlayHit && target === sheetOverlayHit) {
      closeSheet();
      return;
    }

    /* Sheet ATC button */
    var sheetAtb = target.closest('[data-hcs-sheet-atb]');
    if (sheetAtb) {
      e.preventDefault();
      var overlay = sheetAtb.closest('.hcs-sheet-overlay');
      if (!overlay) return;
      var body = overlay.querySelector('.hcs-sheet__body');
      var vid = resolveVariantId(body);
      if (vid) {
        /* Kick off the add, then close the sheet as soon as the request
           resolves — success or not. We listen for cart:updated (fired on
           success) to close right away, so the cart drawer opening is the
           confirmation of the add. On failure the sheet stays open so the
           user can see the error on the button and retry without losing
           their variant selection. */
        var onCartUpdated = function () {
          document.removeEventListener('cart:updated', onCartUpdated);
          closeSheet();
        };
        document.addEventListener('cart:updated', onCartUpdated);
        ajaxAdd(vid, sheetAtb);
      }
      return;
    }

    /* Heat bar segment */
    var heatSeg = target.closest('.hcs-card__heat-seg');
    if (heatSeg) {
      if (heatSeg.classList.contains('hcs-sel-btn--soldout')) { e.preventDefault(); return; }
      e.preventDefault();
      selectOptionButton(heatSeg, '.hcs-card__heat-seg', 'hcs-card__heat-seg--on');
      return;
    }

    /* Size pill */
    var sizePill = target.closest('.hcs-card__size-seg');
    if (sizePill) {
      if (sizePill.classList.contains('hcs-sel-btn--soldout')) { e.preventDefault(); return; }
      e.preventDefault();
      selectOptionButton(sizePill, '.hcs-card__size-seg', 'hcs-card__size-seg--on');
      return;
    }

    /* Toggle bar segment */
    var toggleSeg = target.closest('.hcs-card__toggle-seg');
    if (toggleSeg) {
      if (toggleSeg.classList.contains('hcs-sel-btn--soldout')) { e.preventDefault(); return; }
      e.preventDefault();
      selectOptionButton(toggleSeg, '.hcs-card__toggle-seg', 'hcs-card__toggle-seg--on');
      return;
    }

    /* ATC button — card ATB, not sheet ATB (handled above) */
    var atbBtn = target.closest('[data-hcs-add]');
    if (atbBtn) {
      if (atbBtn.hasAttribute('disabled')) return;
      e.preventDefault();

      var card = atbBtn.closest('[data-hcs-card]');
      var wrap = atbBtn.closest('[data-hcs-atb-wrap]');
      var hasVariants = atbBtn.getAttribute('data-hcs-has-variants') === 'true';
      var scope = card ? card.closest('[data-hcs-scope]') : null;

      /* Single variant — just add */
      if (!hasVariants) {
        ajaxAdd(atbBtn.getAttribute('data-hcs-vid'), atbBtn);
        return;
      }

      /* Mobile + sheet enabled → unified X-style quick-add picker
         (hcs-qa-picker). Legacy cloned sheet only as a fallback when the
         picker or the card's qap payload is missing. */
      if (!isDesktop() && wantsSheetM(scope)) {
        if (window.HCSQaPicker && card && card.hasAttribute('data-qap-scope')) {
          window.HCSQaPicker.open(atbBtn, e);
          return;
        }
        if (shouldUseSheet(scope)) {
          openSheet(card, atbBtn);
          return;
        }
      }

      /* Desktop: click resolves variant and adds (panel already open via hover) */
      if (isDesktop()) {
        var dvid = resolveVariantId(wrap);
        if (dvid) ajaxAdd(dvid, atbBtn);
        return;
      }

      /* Mobile, no sheet: first tap activates, second tap adds */
      var isActive = card && card.classList.contains('hcs-card--active');
      if (!isActive) {
        activateCard(card);
      } else {
        var mvid = resolveVariantId(wrap);
        if (mvid) {
          ajaxAdd(mvid, atbBtn);
          deactivateCard(card);
        }
      }
      return;
    }

    /* Click inside an open ATB panel — swallow so it doesn't navigate */
    if (target.closest('.hcs-card__atb-panel')) {
      return;
    }

    /* Card click → navigate to product page (but not if clicking inside
       the ATB area, an open link, or the variant panel) */
    var cardHit = target.closest('[data-hcs-card]');
    if (cardHit) {
      if (target.closest('[data-hcs-atb-wrap], .hcs-card__atb-btn, .hcs-card__atb-area, a, select, label, [data-hcs-drawer-vsel]')) {
        return;
      }
      var url = cardHit.getAttribute('data-hcs-url');
      if (url) window.location.href = url;
      return;
    }

    /* Clicked outside any card — close active panels globally */
    var allActive = document.querySelectorAll('.hcs-card--active');
    for (var n = 0; n < allActive.length; n++) {
      allActive[n].classList.remove('hcs-card--active');
      var cch = allActive[n].querySelector('.hcs-card__btn-chevron');
      if (cch && cch.textContent === '→') cch.textContent = '▾';
    }
  }, false); /* bubbling phase only — zero capture, zero stopImmediatePropagation */

  /* ──────────────────────────────────────────────────────────────────────
     CHANGE delegation — dropdown fallback variant pickers
     ────────────────────────────────────────────────────────────────────── */

  document.addEventListener('change', function (e) {
    var sel = e.target;
    if (!sel.classList || !sel.classList.contains('hcs-card__vp-sel')) return;

    var wrap = sel.closest('[data-hcs-atb-wrap], .hcs-sheet__body');
    if (!wrap) return;
    if (wrap.classList.contains('hcs-sheet__body')) {
      syncSheetFromBody(wrap.closest('.hcs-sheet-overlay'));
    } else {
      syncBtnForVariant(wrap);
    }
  }, false);

  /* ──────────────────────────────────────────────────────────────────────
     DESKTOP HOVER — mouseover/mouseout delegate (not mouseenter since
     that doesn't bubble). Only activates cards with variants so single-
     variant cards don't get a panel they don't need.

     Close rule: once the cursor has ENTERED the variant panel at least
     once since activation, any subsequent move to an element outside
     the panel closes it. This means:
       - Hover button → panel opens, stays open while cursor is on button
         (even though cursor is not yet inside panel)
       - Move cursor onto panel → cursor has now "entered" the panel
       - Move cursor anywhere outside the panel (image, hover-fill,
         another card, off the card entirely) → panel closes

     We track the "has entered panel" state with a flag on the card
     element itself so it survives across event-handler invocations
     without any closure/bind state.
     ────────────────────────────────────────────────────────────────────── */

  document.addEventListener('mouseover', function (e) {
    if (!isDesktop()) return;

    // Activation on button hover (only for cards with variants)
    var btn = e.target.closest('[data-hcs-add]');
    if (!btn) return;
    if (btn.getAttribute('data-hcs-has-variants') !== 'true') return;
    var card = btn.closest('[data-hcs-card]');
    if (!card) return;
    if (!card.classList.contains('hcs-card--active')) {
      activateCard(card);
    }
  }, false);

  document.addEventListener('mouseout', function (e) {
    if (!isDesktop()) return;
    var card = e.target.closest('[data-hcs-card]');
    if (!card) return;
    if (!card.classList.contains('hcs-card--active')) return;

    /* Close the panel when the cursor leaves the ATC button OR the
       variant panel — not when it leaves the card entirely. The
       button + panel together form the "intent to buy" surface; moving
       off both (e.g., onto the card image) means the user is back to
       browsing and the panel should collapse.

       The button and panel overlap by 22px (margin-bottom: -22px on
       .hcs-card__atb-panel) so moving between them is continuous —
       mouseout/mouseover fire cleanly without any dead-gap flicker. */
    var to = e.relatedTarget;
    var btn = card.querySelector('[data-hcs-add]');
    var panel = card.querySelector('.hcs-card__atb-panel');
    var stayInside =
      (btn && to && (to === btn || (btn.contains && btn.contains(to)))) ||
      (panel && to && (to === panel || (panel.contains && panel.contains(to))));
    if (stayInside) return;
    deactivateCard(card);
  }, false);

  /* ──────────────────────────────────────────────────────────────────────
     BOTTOM SHEET — swipe-down to dismiss (passive touch listeners)
     ────────────────────────────────────────────────────────────────────── */

  document.addEventListener('touchstart', function (e) {
    var sheet = e.target.closest('.hcs-sheet');
    if (!sheet) return;
    var overlay = sheet.closest('.hcs-sheet-overlay');
    if (!overlay) return;
    overlay._hcsSheetStartY = e.touches[0] ? e.touches[0].clientY : 0;
  }, { passive: true });

  document.addEventListener('touchend', function (e) {
    var sheet = e.target.closest('.hcs-sheet');
    if (!sheet) return;
    var overlay = sheet.closest('.hcs-sheet-overlay');
    if (!overlay) return;
    var startY = overlay._hcsSheetStartY || 0;
    var endY = e.changedTouches && e.changedTouches[0] ? e.changedTouches[0].clientY : 0;
    if (endY - startY > 60) {
      closeSheet();
    }
  }, { passive: true });

  /* ──────────────────────────────────────────────────────────────────────
     Escape key closes an open sheet
     ────────────────────────────────────────────────────────────────────── */

  document.addEventListener('keydown', function (e) {
    if (e.key === 'Escape' || e.keyCode === 27) {
      if (document.querySelector('.hcs-sheet-overlay--open')) {
        closeSheet();
      }
    }
  }, false);

  /* ──────────────────────────────────────────────────────────────────────
     Initial pass — reflect sold-out option values + ATB state from the
     default selection on every card, so impossible combos are flagged
     before the first interaction. Idempotent; safe to re-run.
     ────────────────────────────────────────────────────────────────────── */
  function initSoldOut() {
    var wraps = document.querySelectorAll('.hcs-card__atb-wrap[data-hcs-atb-wrap]');
    for (var i = 0; i < wraps.length; i++) {
      if (wraps[i].querySelector('[data-hcs-variants]')) {
        ensureAvailableDefault(wraps[i]);
        syncBtnForVariant(wraps[i]);
      }
    }
  }
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initSoldOut, false);
  } else {
    initSoldOut();
  }

})();
